import { useEffect, useState, useCallback } from 'react';

/**
 * useQueueListener Hook - WebSocket Queue Listener for Real-Time Updates
 *
 * Connects to Cloudflare Queues via WebSocket for real-time message updates.
 *
 * TODO: After Cloudflare Queues WebSocket configured:
 * 1. Replace mock WebSocket URL with actual queue WebSocket endpoint
 * 2. Add authentication token to WebSocket connection
 * 3. Enable connection retry logic
 * 4. Add heartbeat/keepalive mechanism
 *
 * For now, this is a placeholder implementation that can be used
 * for development/testing with mock data.
 *
 * Usage:
 * const { messages, isConnected, error } = useQueueListener('design-generation');
 */

export interface QueueMessage {
  message_id: string;
  timestamp: number;
  event_type: string;
  payload: Record<string, any>;
  _retry_count: number;
  _max_retries: number;
  _published_at: number;
}

interface UseQueueListenerOptions {
  enabled?: boolean;
  autoReconnect?: boolean;
  reconnectDelay?: number;
}

export function useQueueListener(
  queueName: string,
  options: UseQueueListenerOptions = {}
) {
  const { enabled = false, autoReconnect = true, reconnectDelay = 5000 } = options;

  const [messages, setMessages] = useState<QueueMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);

  const connect = useCallback(() => {
    // TODO: Replace with actual Cloudflare Queue WebSocket endpoint
    // const wsUrl = `wss://queues.cksaas.dev/${queueName}`;

    // For now, this is disabled until Better Auth API + Queue WebSocket is deployed
    console.log(`[useQueueListener] Queue listener for "${queueName}" initialized (currently disabled)`);
    console.log('[useQueueListener] Will connect to WebSocket after Cloudflare Queues configured');

    // Mock connection (for development)
    if (false) { // Set to true to enable mock WebSocket for local testing
      try {
        const mockWs = new WebSocket('ws://localhost:8080');

        mockWs.onopen = () => {
          console.log(`[useQueueListener] Connected to ${queueName} queue`);
          setIsConnected(true);
          setError(null);
        };

        mockWs.onmessage = (event) => {
          try {
            const message: QueueMessage = JSON.parse(event.data);
            console.log(`[useQueueListener] Received message:`, message);
            setMessages((prev) => [...prev, message]);
          } catch (error) {
            console.error('[useQueueListener] Failed to parse message:', error);
          }
        };

        mockWs.onerror = (event) => {
          console.error(`[useQueueListener] WebSocket error:`, event);
          setError('WebSocket connection error');
          setIsConnected(false);
        };

        mockWs.onclose = () => {
          console.log(`[useQueueListener] Disconnected from ${queueName} queue`);
          setIsConnected(false);
          setWs(null);

          // Auto-reconnect if enabled
          if (autoReconnect) {
            console.log(`[useQueueListener] Reconnecting in ${reconnectDelay}ms...`);
            setTimeout(connect, reconnectDelay);
          }
        };

        setWs(mockWs);
      } catch (error) {
        console.error('[useQueueListener] Failed to create WebSocket:', error);
        // Type guard for error handling
        const errorMessage = (error as Error)?.message || 'WebSocket creation failed';
        setError(errorMessage);
      }
    }
  }, [queueName, autoReconnect, reconnectDelay]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      if (ws) {
        console.log('[useQueueListener] Closing WebSocket connection');
        ws.close();
      }
    };
  }, [enabled, connect]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const disconnect = useCallback(() => {
    if (ws) {
      ws.close();
      setWs(null);
    }
  }, [ws]);

  return {
    messages,
    isConnected,
    error,
    clearMessages,
    disconnect,
  };
}

/**
 * Hook for filtering queue messages by event type
 */
export function useQueueMessages(
  queueName: string,
  eventType: string,
  options?: UseQueueListenerOptions
) {
  const { messages, ...rest } = useQueueListener(queueName, options);

  const filteredMessages = messages.filter((msg) => msg.event_type === eventType);

  return {
    messages: filteredMessages,
    ...rest,
  };
}

/**
 * Hook for latest queue message of specific type
 */
export function useLatestQueueMessage(
  queueName: string,
  eventType: string,
  options?: UseQueueListenerOptions
) {
  const { messages, ...rest } = useQueueMessages(queueName, eventType, options);

  const latestMessage = messages.length > 0 ? messages[messages.length - 1] : null;

  return {
    message: latestMessage,
    ...rest,
  };
}
